import React from "react";
import {ExperimentOutlined} from "@ant-design/icons";
import {Result} from "antd";
import {Card} from "@clockout-dev/design-elements";
import EmptyState from "../../common/emptyState/EmptyState";
import RunProgress from "./RunProgress";
import RulingBanner from "./RulingBanner";
import BatchFileStrip from "./BatchFileStrip";
import ContiguityStrip from "./ContiguityStrip";
import ClassificationCard from "./ClassificationCard";
import ExtractedFieldsCard from "./ExtractedFieldsCard";
import ContradictionsCard from "./ContradictionsCard";
import FraudSignalsCard from "./FraudSignalsCard";
import MemberVsInternalCard from "./MemberVsInternalCard";
import DerivedOutcomeCard from "./DerivedOutcomeCard";
import SideEffectLedger from "./SideEffectLedger";
import JsonViewerCard from "./JsonViewerCard";
import {buildContiguityModel} from "../sor.model";
import styles from "../styles/results.module.scss";

/**
 * Right column state machine: empty state → live progress → error → the full
 * internal report. Every report key is optional — a partial payload renders
 * whatever it carries and never crashes the panel.
 *
 * @param {Object} props
 * @param {Object | null} props.run — controller run state (non-null while running)
 * @param {Object | null} props.report — completed simulation report
 * @param {{title: string, detail: string, kind: string} | null} props.runError
 * @param {number} props.selectedFileIndex
 * @param {(index: number) => void} props.onSelectFile
 * @param {() => void} props.onCancelRun
 * @param {(data: *) => void} props.onCopyJson
 */
function ResultsPanel({run, report, runError, selectedFileIndex, onSelectFile, onCancelRun, onCopyJson}) {
    if (run) {
        return (
            <div className={styles.panel}>
                <RunProgress run={run} onCancel={onCancelRun} />
            </div>
        );
    }

    if (runError) {
        return (
            <div className={styles.panel}>
                <Card className={styles.errorCard}>
                    <Result status={runError.kind === "access" ? "403" : "warning"} title={runError.title} subTitle={runError.detail} />
                </Card>
            </div>
        );
    }

    if (!report) {
        return (
            <div className={styles.panel}>
                <EmptyState
                    showIcon
                    icon={<ExperimentOutlined className={styles.emptyIcon} />}
                    title="No simulation yet"
                    description="Author the SOR on the left, drop a document, and run the real Claude-vision verification to see everything production redacts."
                />
            </div>
        );
    }

    const verdicts = Array.isArray(report.verdicts) ? report.verdicts : [];
    const verdictIndex = verdicts.length ? Math.min(Math.max(selectedFileIndex, 0), verdicts.length - 1) : 0;
    const verdict = verdicts[verdictIndex] || null;
    const contiguityModel = report.batch_analysis ? buildContiguityModel(report.batch_analysis, report.db_post_state?.paystubs) : null;

    return (
        <div className={styles.panel}>
            <div className={styles.reportStack}>
                <RulingBanner result={report.result} mode={report.mode} durationMs={report.duration_ms} />
                <BatchFileStrip files={report.request?.files} verdicts={verdicts} selectedIndex={verdictIndex} onSelect={onSelectFile} />
                {contiguityModel ? <ContiguityStrip model={contiguityModel} /> : null}
                <ClassificationCard verdict={verdict} mode={report.mode} />
                <ExtractedFieldsCard verdict={verdict} />
                <ContradictionsCard contradictions={verdict?.contradictions} />
                <FraudSignalsCard verdict={verdict} silentSignals={report.silent_signals} />
                <MemberVsInternalCard memberVisible={report.member_visible} result={report.result} silentSignals={report.silent_signals} />
                {report.batch_analysis ? <DerivedOutcomeCard batchAnalysis={report.batch_analysis} accepted={report.result?.accepted === true} /> : null}
                <SideEffectLedger sideEffects={report.side_effects} />
                <JsonViewerCard title="SOR snapshot sent to the model" data={verdict?.sor_snapshot_sent} onCopy={onCopyJson} />
                <JsonViewerCard title="Raw simulation report" data={report} onCopy={onCopyJson} />
            </div>
        </div>
    );
}

export default ResultsPanel;
